import type {
  JobApi,
  JobAutomationUpdateRequestApi,
} from '@/modules/jobs/types';
import type { LinkedInStatus } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import type { components } from '@/types/apiSchema';

type ScreeningMethodApi = components['schemas']['ScreeningMethod'];

/** Plain-language automation choices in place of the recruiter's flags. */
export interface JobAutomation {
  outreach: 'finlay' | 'me';
  /** `human` is the job owner screening candidates themselves via their booking link. */
  prescreen: 'ai' | 'human' | 'skip';
}

export type StageKey = keyof JobAutomation;

/** The stages in the order the Automation tab shows them. */
export const stageKeys: StageKey[] = ['outreach', 'prescreen'];

export const stageLabels: Record<StageKey, string> = {
  outreach: 'Who reaches out',
  prescreen: 'Pre-screen call',
};

export interface Preset {
  key: 'handsOff' | 'reviewAll';
  title: string;
  /** Who this is for, in one line. */
  fit: string;
  values: JobAutomation;
  recommended?: boolean;
}

const autopilot: Preset = {
  key: 'handsOff',
  title: 'Autopilot',
  fit: 'Finlay handles outreach and pre-screening so you can review candidates with full context.',
  values: { outreach: 'finlay', prescreen: 'ai' },
  recommended: true,
};

const sourcingOnly: Preset = {
  key: 'reviewAll',
  title: 'Sourcing only',
  fit: 'Finlay finds candidates. You handle outreach and candidate conversations.',
  values: { outreach: 'me', prescreen: 'skip' },
};

export const presets: Preset[] = [autopilot, sourcingOnly];

/**
 * The name for values that match no preset, such as a job set up before the
 * Automation tab offered only setups. It is not stored.
 */
export const customChoice = {
  key: 'custom',
  title: 'Custom',
} as const;

/** One of the Automation tab's setups: a preset, or Finlay reaching out while you screen. */
export interface Setup {
  key: Preset['key'] | 'screenYourself';
  title: string;
  /** What Finlay and you each do, in two short sentences. */
  description: string;
  values: JobAutomation;
  recommended?: boolean;
}

/**
 * The Automation tab's choices. Screening yourself is not a preset: the
 * job editor creates jobs before a booking link can be asked for.
 */
export const setups: Setup[] = [
  {
    ...autopilot,
    description:
      'Finlay messages matching candidates, follows up, and runs a 10-minute AI call on your must-haves. You review each one with a summary and recording.',
  },
  {
    key: 'screenYourself',
    title: 'Finlay reaches out, you screen',
    description:
      'Finlay messages and follows up. Candidates who reply get your booking link, and you add notes after the call.',
    values: { outreach: 'finlay', prescreen: 'human' },
  },
  {
    ...sourcingOnly,
    description:
      'Finlay builds your shortlist. You contact the people you like and run every conversation yourself.',
  },
];

/** The preset new self-service jobs start on. */
export const recommendedPreset: Preset = presets.find(
  (preset) => preset.recommended,
)!;

const isSameAutomation = (a: JobAutomation, b: JobAutomation) =>
  stageKeys.every((key) => a[key] === b[key]);

export const matchingSetup = (values: JobAutomation) =>
  setups.find((setup) => isSameAutomation(setup.values, values));

/** Plain-language summary of the selected setup. */
export const describeAutomation = (values: JobAutomation) => {
  const sentences = [
    values.outreach === 'finlay'
      ? 'Finlay finds and contacts candidates'
      : 'Finlay finds candidates, and you contact them',
  ];
  if (values.prescreen === 'ai')
    sentences.push('Finlay runs a pre-screen call');
  if (values.prescreen === 'human')
    sentences.push('You run the pre-screen call via your booking link');
  return `${sentences.join('. ')}.`;
};

const prescreenByMethod: Record<
  ScreeningMethodApi,
  JobAutomation['prescreen']
> = {
  Ai: 'ai',
  HiringManager: 'human',
  // Agency-only; the self-service picker has no recruiter screening choice.
  InternalRecruiter: 'skip',
  None: 'skip',
};

const methodByPrescreen: Record<
  JobAutomation['prescreen'],
  ScreeningMethodApi
> = { ai: 'Ai', human: 'HiringManager', skip: 'None' };

export const automationFromJob = (job: JobApi): JobAutomation => ({
  outreach: job.isReachOutAutomated ? 'finlay' : 'me',
  prescreen: prescreenByMethod[job.screeningMethod],
});

/** Human pre-screening sends the booking link in Finlay's follow-ups, so it needs Finlay outreach. */
export const isPrescreenOptionAvailable = (
  outreach: JobAutomation['outreach'],
  prescreen: JobAutomation['prescreen'],
) => prescreen !== 'human' || outreach === 'finlay';

/**
 * Whether choosing Finlay outreach offers to connect LinkedIn first. Finlay
 * sends from the job owner's LinkedIn, so only the owner can connect it, and
 * an unanswered lookup is not a missing account. It never blocks the choice:
 * the API holds outreach until the account is connected.
 */
export const offersLinkedInConnect = ({
  outreachOn,
  isOwner,
  linkedInStatus,
}: {
  /** Finlay reaches out on the job. */
  outreachOn: boolean;
  isOwner: boolean;
  linkedInStatus: LinkedInStatus;
}) => outreachOn && isOwner && linkedInStatus === 'disconnected';

/** What the workspace plan includes; undefined is unknown and allows the choice. */
export interface AutomationPlanLimits {
  includesOutreach?: boolean;
  includesAiPrescreen?: boolean;
}

/**
 * Drops choices the plan does not include so a save never asks the API for
 * them: no Finlay outreach falls back to `me`, no AI calls to `skip`, and the
 * result stays valid under `isPrescreenOptionAvailable`.
 */
export const limitToPlan = (
  values: JobAutomation,
  limits: AutomationPlanLimits,
): JobAutomation => {
  const outreach = limits.includesOutreach === false ? 'me' : values.outreach;
  const prescreen =
    limits.includesAiPrescreen === false && values.prescreen === 'ai'
      ? 'skip'
      : values.prescreen;
  return {
    outreach,
    prescreen: isPrescreenOptionAvailable(outreach, prescreen)
      ? prescreen
      : 'skip',
  };
};

// Stage fields only: the job editor spreads presets into its workflow draft, whose
// template is a string, so the patch must not claim the messaging-template fields.
type StagePatch = Pick<
  JobAutomationUpdateRequestApi,
  | 'isReachOutAutomated'
  | 'isMessagingAutomated'
  | 'isInvitationFollowUpEnabled'
  | 'isPrescreeningEnabled'
  | 'screeningMethod'
>;

/** The fields one stage change touches; the others are left out of the patch. */
const automationPatch = <K extends StageKey>(
  stage: K,
  value: JobAutomation[K],
): StagePatch => {
  switch (stage) {
    case 'outreach': {
      const enabled = value === 'finlay';
      return {
        isReachOutAutomated: enabled,
        isMessagingAutomated: enabled,
        isInvitationFollowUpEnabled: enabled,
      };
    }
    case 'prescreen': {
      const prescreen = value as JobAutomation['prescreen'];
      // `screeningMethod` wins on the API; the flag keeps the create request (which maps flags only) in sync.
      return {
        isPrescreeningEnabled: prescreen === 'ai',
        screeningMethod: methodByPrescreen[prescreen],
      };
    }
  }
};

/** The fields the given stages touch; the others are left out of the patch. */
export const automationPatchFor = (
  change: Partial<JobAutomation>,
): StagePatch =>
  stageKeys.reduce<StagePatch>((patch, key) => {
    const value = change[key];
    return value === undefined
      ? patch
      : { ...patch, ...automationPatch(key, value) };
  }, {});

export const presetPatch = (values: JobAutomation): StagePatch =>
  automationPatchFor(values);

/** What switching setups sets in motion, one line each in the confirm dialog. */
export type Consequence =
  | 'outreachStarts'
  | 'outreachStops'
  | 'aiCallStarts'
  | 'aiCallStops'
  | 'bookingLinkStarts'
  | 'bookingLinkStops';

/** A switch that takes work back from Finlay, so work already under way is said to finish as planned. */
export const isHandBack = (from: JobAutomation, to: JobAutomation) =>
  (from.outreach === 'finlay' && to.outreach === 'me') ||
  (from.prescreen === 'ai' && to.prescreen !== 'ai');

export const changedStages = (from: JobAutomation, to: JobAutomation) =>
  stageKeys.filter((key) => from[key] !== to[key]);

/** What changes for new candidates, outreach first. Taking AI calls away says so only when nothing replaces them. */
export const consequencesOf = (
  from: JobAutomation,
  to: JobAutomation,
): Consequence[] => {
  const consequences: Consequence[] = [];
  if (from.outreach !== to.outreach) {
    consequences.push(
      to.outreach === 'finlay' ? 'outreachStarts' : 'outreachStops',
    );
  }
  if (from.prescreen !== to.prescreen) {
    if (to.prescreen === 'ai') consequences.push('aiCallStarts');
    if (to.prescreen === 'human') consequences.push('bookingLinkStarts');
    if (to.prescreen === 'skip') {
      consequences.push(
        from.prescreen === 'ai' ? 'aiCallStops' : 'bookingLinkStops',
      );
    }
  }
  return consequences;
};
